// SPDX-License-Identifier: Apache-2.0

// Edge extraction — port of `vault-links.ts` from the old React repo. Pure
// over `VaultDoc[]`; no DOM, no Solid. The orbit feeds explicit edges to the
// WGSL force kernel; the modal's "related notes" panel will reuse this once
// it lands.

import type { VaultDoc } from "@tether/storage";

const WIKILINK = /\[\[([^\]|#]+)(?:[|#][^\]]*)?\]\]/g;
const MD_LINK = /\[[^\]]*\]\(([^)\s]+)\)/g;

function normTitle(s: string): string {
  // Drop fragment/query, take final path segment, strip .md, lowercase.
  const noFragment = s.split(/[#?]/)[0] ?? s;
  const parts = noFragment.split("/");
  const last = parts[parts.length - 1] ?? noFragment;
  return last.trim().replace(/\.md$/i, "").toLowerCase();
}

function buildTitleIndex(docs: VaultDoc[]): Map<string, string> {
  const idx = new Map<string, string>();
  for (const d of docs) idx.set(normTitle(d.title), d.id);
  return idx;
}

export function extractLinkedIds(
  content: string,
  selfId: string,
  titleIndex: Map<string, string>,
): Set<string> {
  const out = new Set<string>();

  WIKILINK.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = WIKILINK.exec(content))) {
    const id = titleIndex.get(normTitle(m[1] ?? ""));
    if (id && id !== selfId) out.add(id);
  }

  MD_LINK.lastIndex = 0;
  while ((m = MD_LINK.exec(content))) {
    const raw = m[1] ?? "";
    if (/^https?:/i.test(raw) || /^mailto:/i.test(raw) || raw.startsWith("#")) continue;
    const stem = raw.split(/[#?]/)[0] ?? raw;
    const id = titleIndex.get(normTitle(stem));
    if (id && id !== selfId) out.add(id);
  }

  return out;
}

/** Undirected edge list, deduped, no self-loops. */
export function buildEdges(docs: VaultDoc[]): Array<[string, string]> {
  const titleIndex = buildTitleIndex(docs);
  const edges: Array<[string, string]> = [];
  const seen = new Set<string>();

  for (const d of docs) {
    const out = extractLinkedIds(d.content, d.id, titleIndex);
    for (const targetId of out) {
      const a = d.id < targetId ? d.id : targetId;
      const b = d.id < targetId ? targetId : d.id;
      const key = `${a}|${b}`;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push([d.id, targetId]);
    }
  }

  return edges;
}
